// 📁 src/services/api.js
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { productLoginPath, shouldKeepPublicAuthPage } from "../utils/productLogin";
import { isElloAppDomain, isElloPwaDomain } from "../utils/elloDomains";

/**
 * API central do front.
 * Projeto atual usa CRA/react-scripts, então este arquivo evita import.meta
 * para não quebrar build. A base em produção deve vir de REACT_APP_API_BASE_URL.
 */
const RAW_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";
const API_BASE_URL = String(RAW_BASE_URL || "")
  .replace(/([^:]\/)\/+/, "$1")
  .trim();

const isProd =
  process.env.NODE_ENV === "production" ||
  process.env.REACT_APP_ENV === "production";

const DEFAULT_DEV_BASE = "http://localhost:8090";

// Os dois produtos ÉLLO publicados em subdomínios usam a API pública da
// infraestrutura existente. A verificação é pelo hostname exato (não por
// query string), para não modificar o comportamento do Gestor I9 nem de
// outras versões que compartilham este mesmo bundle React.
// A configuração REACT_APP_API_BASE_URL pertence ao BUILD; se o bundle
// compartilhado for gerado com localhost, só a ÉLLO corrige sua origem.
const isElloSubdomain = isElloAppDomain() || isElloPwaDomain();
const ELLO_PRODUCTION_API_URL = "https://api.lionofcode.com";

if (isProd && !API_BASE_URL && !isElloSubdomain) {
  // eslint-disable-next-line no-console
  console.error(
    "❌ REACT_APP_API_BASE_URL não definido em produção. " +
      "Defina no .env.production ou no ambiente do build."
  );
  throw new Error("API base URL ausente em produção.");
}

const RESOLVED_BASE_URL = isElloSubdomain
  ? ELLO_PRODUCTION_API_URL
  : API_BASE_URL || DEFAULT_DEV_BASE;

const LS_KEYS = [
  "token",
  "user",
  "permissions",
  "selectedEntidade",
  "selectedUnidade",
  "Entidade",
  "Unidade",
  "EntidadeId",
  "UnidadeId",
  "entidadeId",
  "unidadeId",
];

const api = axios.create({
  baseURL: RESOLVED_BASE_URL,
  timeout: 30000,
});

export const updateToken = (token) => {
  if (token && String(token).trim()) {
    api.defaults.headers.common.Authorization = `Bearer ${token}`;
    localStorage.setItem("token", token);
  } else {
    delete api.defaults.headers.common.Authorization;
    localStorage.removeItem("token");
  }
};

const clearAuthStorage = () => {
  for (const key of LS_KEYS) localStorage.removeItem(key);
  sessionStorage.removeItem("senhaAtual");
  sessionStorage.removeItem("pwabeleza:firstAccess");
};

const nowEpoch = () => Math.floor(Date.now() / 1000);

const isTokenValid = (token) => {
  if (!token) return false;
  try {
    const decoded = jwtDecode(token);
    return Boolean(decoded?.exp && decoded.exp > nowEpoch());
  } catch {
    return false;
  }
};

const isAuthRoute = (url = "") => {
  const value = String(url || "").toLowerCase();
  return (
    value.startsWith("/api/auth/") ||
    value === "/api/auth" ||
    value.startsWith("/auth/") ||
    value === "/auth"
  );
};

// O varejo/PDV novo é claim-scoped: EntidadeId/UnidadeId são resolvidos pelo
// backend exclusivamente a partir do JWT. Mantemos os headers legados para os
// módulos antigos, mas removemos explicitamente nesses endpoints para evitar
// que qualquer valor do browser seja confundido com autoridade de tenant.
const isClaimScopedRoute = (url = "") => {
  const value = String(url || "").toLowerCase();
  return value.startsWith("/api/pdv") || value.startsWith("/api/varejo");
};

// Portal de cotação do fornecedor é realmente anônimo. Um token JWT antigo
// salvo pelo navegador não deve bloquear ou redirecionar esse acesso público.
const isAnonymousApiRoute = (url = "") => {
  let pathname;
  try {
    pathname = new URL(String(url || ""), RESOLVED_BASE_URL).pathname.replace(/\/$/, "").toLowerCase();
  } catch {
    return false;
  }
  return [
    "/api/usuario/forgot-password",
    "/api/usuario/validate-reset-code",
    "/api/usuario/reset-password",
  ].includes(pathname) ||
    pathname.startsWith("/api/varejo/compras/cotacao-publica/");
};

const redirectIfPrivateRoute = () => {
  if (typeof window === "undefined") return;
  const path = window.location.pathname;
  if (shouldKeepPublicAuthPage(path)) return;
  const destination = productLoginPath(path);
  if (destination !== path) window.location.href = destination;
};

const isLoginRequest = (url = "") => {
  try {
    return ["/api/auth/login", "/api/auth/login-operador"].includes(new URL(String(url), RESOLVED_BASE_URL).pathname.replace(/\/$/, "").toLowerCase());
  } catch {
    return false;
  }
};

const getContextFromToken = (token) => {
  try {
    const decoded = jwtDecode(token) || {};

    const entidade =
      decoded.entidadeid ??
      decoded.entidadeId ??
      decoded.EntidadeId ??
      decoded.EntidadeID ??
      decoded.Entidade ??
      decoded.entidade ??
      null;

    const unidade =
      decoded.unidadeid ??
      decoded.unidadeId ??
      decoded.UnidadeId ??
      decoded.UnidadeID ??
      decoded.Unidade ??
      decoded.unidade ??
      null;

    return {
      entidade: entidade ? String(entidade) : null,
      unidade: unidade ? String(unidade) : null,
    };
  } catch {
    return { entidade: null, unidade: null };
  }
};

const getContextFromStorage = () => {
  const entidade =
    localStorage.getItem("Entidade") ||
    localStorage.getItem("EntidadeId") ||
    localStorage.getItem("entidadeId") ||
    localStorage.getItem("selectedEntidade");

  const unidade =
    localStorage.getItem("Unidade") ||
    localStorage.getItem("UnidadeId") ||
    localStorage.getItem("unidadeId") ||
    localStorage.getItem("selectedUnidade");

  return {
    entidade: entidade ? String(entidade) : null,
    unidade: unidade ? String(unidade) : null,
  };
};

const persistContext = ({ entidade, unidade }) => {
  if (entidade) {
    localStorage.setItem("selectedEntidade", entidade);
    localStorage.setItem("Entidade", entidade);
    localStorage.setItem("EntidadeId", entidade);
    localStorage.setItem("entidadeId", entidade);
  }

  if (unidade) {
    localStorage.setItem("selectedUnidade", unidade);
    localStorage.setItem("Unidade", unidade);
    localStorage.setItem("UnidadeId", unidade);
    localStorage.setItem("unidadeId", unidade);
  }
};

api.interceptors.request.use(
  (config) => {
    config.headers = config.headers || {};

    if (isProd) {
      const base = String(api.defaults.baseURL || "").toLowerCase();
      if (base.includes("localhost") || base.includes("127.0.0.1")) {
        console.error("❌ BaseURL inválida em produção:", api.defaults.baseURL);
        const error = new Error("A configuração da API está marcada como produção, mas aponta para localhost. Corrija o ambiente do frontend.");
        error.code = "API_CONFIGURATION_ERROR";
        return Promise.reject(error);
      }
    }

    const rawAuth = api.defaults.headers.common.Authorization;
    const tokenFromDefaults =
      typeof rawAuth === "string" && rawAuth.startsWith("Bearer ")
        ? rawAuth.slice("Bearer ".length)
        : null;

    const loginRequest = isLoginRequest(config.url);
    const anonymousRequest = isAnonymousApiRoute(config.url);
    const storedToken = tokenFromDefaults || localStorage.getItem("token");
    // Entrar novamente e endpoints públicos não dependem da sessão anterior.
    const token = loginRequest || anonymousRequest ? null : storedToken;
    if (loginRequest || anonymousRequest) delete config.headers.Authorization;

    if (token && !isTokenValid(token)) {
      console.warn("🔴 Token expirado antes do envio. Limpando sessão.");
      clearAuthStorage();
      delete api.defaults.headers.common.Authorization;
      redirectIfPrivateRoute();
      return Promise.reject(new Error("Token expirado."));
    }

    if (token) config.headers.Authorization = `Bearer ${token}`;

    if (!isAuthRoute(config.url)) {
      const claimScoped = isClaimScopedRoute(config.url);
      const tokenContext = token
        ? getContextFromToken(token)
        : { entidade: null, unidade: null };
      const storageContext = getContextFromStorage();

      const finalEntidade = tokenContext.entidade || storageContext.entidade;
      const finalUnidade = tokenContext.unidade || storageContext.unidade;

      persistContext({ entidade: tokenContext.entidade, unidade: tokenContext.unidade });

      if (claimScoped) {
        for (const header of ["Entidade", "EntidadeId", "Unidade", "UnidadeId"]) {
          delete config.headers[header];
        }
      } else {
        if (finalEntidade) {
          config.headers.Entidade = finalEntidade;
          config.headers.EntidadeId = finalEntidade;
        }

        if (finalUnidade) {
          config.headers.Unidade = finalUnidade;
          config.headers.UnidadeId = finalUnidade;
        }
      }
    }

    if (!(config.data instanceof FormData) && !config.headers["Content-Type"]) {
      config.headers["Content-Type"] = "application/json";
    }

    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !isLoginRequest(originalRequest.url) &&
      !isAnonymousApiRoute(originalRequest.url) &&
      !originalRequest.skipAuthRefresh &&
      !originalRequest._retry &&
      !String(originalRequest?.url || "").toLowerCase().includes("refresh-token")
    ) {
      originalRequest._retry = true;

      try {
        const currentToken = localStorage.getItem("token");
        if (!currentToken) throw new Error("Sem token para refresh.");

        const base = String(api.defaults.baseURL || "").replace(/\/$/, "");
        const { data } = await axios.post(
          `${base}/api/Auth/refresh-token`,
          { token: currentToken },
          { headers: { "Content-Type": "application/json" } }
        );

        const newToken = data?.data?.token || data?.token;
        if (!newToken) throw new Error("Backend não retornou novo token.");

        updateToken(newToken);

        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${newToken}`;

        return api(originalRequest);
      } catch (refreshError) {
        // A falha da infraestrutura ou do S3 não prova expiração da sessão.
        // Nunca apagar um JWT válido por timeout, 429, 5xx ou falha de rede.
        const currentToken = localStorage.getItem("token");
        if (currentToken && isTokenValid(currentToken)) {
          console.warn("Renovação indisponível; sessão válida preservada.", refreshError);
          return Promise.reject(error);
        }
        clearAuthStorage();
        delete api.defaults.headers.common.Authorization;
        redirectIfPrivateRoute();
        return Promise.reject(refreshError);
      }
    }

    if (error.response?.status === 403) {
      console.error("⛔ 403 Forbidden: verifique Entidade/Unidade no token e nos headers.");
    }

    return Promise.reject(error);
  }
);

const savedToken = localStorage.getItem("token");
if (savedToken) updateToken(savedToken);

export const RESOLVED_API_BASE_URL = RESOLVED_BASE_URL;
export default api;
